import { createContext, useEffect, useLayoutEffect, useState } from "react";
import "../Calendar.css";
import "../DatePicker.css";

import appStyles from "../App.module.css";
import { addDays, convertDateToApiFormat, totalDaysInCurrentYear } from "../app-constants";
import Popup from "./components/Popup";
import Steps from "./components/Step";
import QuoteApi from "./components/api/QuoteApi";
import AUHeader from "./components/common/AUHeader";
import LoadingModal from "./components/modal/LoadingModal";
import btConstants, { btCurrencyProductIds } from "./config/bt-constants";
import { IAUBusinessTravelContext, ITripsData, IinitialQuote } from "./config/interfaces";

function useBreakpointClass() {
  const [bpClass, setBpClass] = useState<string>("");
  useLayoutEffect(() => {
    function updateClass() {
      setBpClass(
        window.innerWidth >= 1400
          ? appStyles.bfpXXL
          : window.innerWidth >= 1200
          ? appStyles.bfpXL
          : window.innerWidth >= 992
          ? appStyles.bfpLG
          : window.innerWidth >= 768
          ? appStyles.bfpMD
          : window.innerWidth >= 576
          ? appStyles.bfpSM
          : appStyles.bfpXS
      );
    }
    window.addEventListener("resize", updateClass);
    updateClass();
    return () => window.removeEventListener("resize", updateClass);
  }, []);
  return bpClass;
}

const initialQuote: IinitialQuote = {
  host_country: null,
  currency: null,
  start_date: new Date(),
  end_date: addDays(new Date(), totalDaysInCurrentYear(new Date())),
  noOfTrips: null,
  noOfEmployee: null,
  productId: 0,
  affiliateId: btConstants?.BTaffiliatedId,
  applicants: [
    {
      is_main: true,
      dob: "1992-08-18",
      trip_cost: 1,
    },
  ],
};

export const AUBusinessTravelContext = createContext<IAUBusinessTravelContext | null>(null);

const AUBusinessTravel = ({ version }: any) => {
  const bpClass = useBreakpointClass();
  const totalSteps = 4;
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [quoteData, setQuoteData] = useState<IinitialQuote>(() => ({
    ...initialQuote,
    affiliateId: new URLSearchParams(window.location.search).get("partner") || btConstants.BTaffiliatedId,
  }));
  const [loader, setLoader] = useState<"quote" | "order" | "payment" | "date" | "trip" | "currency" | null>(null);

  const [order, setOrder] = useState<any>(null);
  const [quoteResult, setQuoteResult] = useState<any>(null);
  const [isPaymentInvoice, setIsPaymentInvoice] = useState<boolean>(false);
  const [tripsData, setTripsData] = useState<ITripsData | null>(null);
  const [partnerLogo, setPartnerLogo] = useState<string | null>(null);
  const [partnerLogoLoading, setPartnerLogoLoading] = useState<boolean>(true);

  useEffect(() => {
    const init = async () => {
      try {
        const productId: any = btCurrencyProductIds.BTAUDProductId;

        const [numberOfTrips, hostCountries, destinationCountries, hostCountryPackages] = await Promise.all([
          QuoteApi.getNumberOfTrips(productId),
          QuoteApi.getHostCountries(productId),
          QuoteApi.getDestinations(productId),
          QuoteApi.getHostCountryPackages(productId, "AU"),
        ]);

        const tripsArray = numberOfTrips.data || [];

        const ranges = hostCountryPackages.data?.[0]?.packages || [];

        const packageLevelsData = hostCountryPackages.data?.[0]?.levels || [];
        const availableLevels = packageLevelsData
          .map((item: any) => Number(item.value))
          .sort((a: number, b: number) => a - b); // Sort ascending to easily find the "next biggest"

        // Check if we have data to build the "newTrip" extension
        let newTrip = null;

        if (tripsArray.length > 0) {
          const lastTrip = tripsArray[tripsArray.length - 1];
          newTrip = {
            id: -1,
            from: lastTrip.till + "+",
            till: lastTrip.till + "+",
            type: "trip_range",
            disabled: true,
          };
        } else {
          // Fallback if no trips exist at all
          newTrip = {
            id: -1,
            from: "1+",
            till: "1+",
            type: "trip_range",
            disabled: true,
          };
        }

        setTripsData({
          numberOfTrips: [...numberOfTrips.data, newTrip],
          hostCountries: hostCountries.data
            ?.filter((country: any) => !country.is_disabled)
            ?.sort((a: any, b: any) => a.name.localeCompare(b.name)),
          destinationCountries: destinationCountries.data[0],
          packageLevels: availableLevels,
        });
      } catch (error) {
        console.error("Error occurred while initializing:", error);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // scroll to top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  useEffect(() => {
    const getPartnerLogo = async () => {
      try {
        setPartnerLogoLoading(true);
        if (!quoteData?.affiliateId || quoteData.affiliateId === "1") {
          setPartnerLogo(null);
          return;
        }
        const partnerResponse = await QuoteApi.getPartnerLogo(quoteData.affiliateId);
        if (partnerResponse.errors) {
          setPartnerLogo(null);
          return;
        }
        setPartnerLogo(partnerResponse?.data?.logo_path || null);
      } catch (error) {
        setPartnerLogo(null);
      } finally {
        setPartnerLogoLoading(false);
      }
    };

    getPartnerLogo();
  }, [quoteData?.affiliateId]);

  const createQuote = async (quoteData: IinitialQuote) => {

    // 1. Get the requested number of trips (handles both object or flat number)
    const requestedAmount = Number(quoteData.noOfTrips?.till || quoteData.noOfTrips) || 0;

    // 2. Find the next highest level
    let matchedLevel = requestedAmount;
    if (tripsData?.packageLevels && tripsData.packageLevels.length > 0) {
      // .find() returns the FIRST item that satisfies the condition. 
      // Because we sorted the array earlier, it will be the next biggest value.
      const nextHighest = tripsData.packageLevels.find((level: number) => level >= requestedAmount);

      // If requestedAmount is higher than your maximum tier, cap it at the highest available tier
      matchedLevel = nextHighest !== undefined
        ? nextHighest
        : tripsData.packageLevels[tripsData.packageLevels.length - 1];
    }


    setQuoteData((prev: any) => ({ ...prev, packageLevel: matchedLevel }));
    
    const apiData = {
      start_date: convertDateToApiFormat(quoteData.start_date),
      end_date: convertDateToApiFormat(quoteData.end_date),
      applicants: quoteData.applicants,
      product: quoteData?.productId,
      host_country: quoteData?.host_country?.code,
      currency: quoteData.currency,
      // destination_groups: [tripsData?.destinationCountries?.name],
      // "host_country_state": "NSW",

      destination_groups: ['WW'],

      "destinations": [ //TODO: get this from API response instead of hardcoding, currently blank
        "AU"
      ],

      "packages": [
        {
          "id": "base_package",
          "conditions": [
            {
              "level": matchedLevel
            }
          ]
        }
      ],

      affiliate: quoteData?.affiliateId,
    };
    const createQuoteResponse = await QuoteApi.createQuote(apiData);
    if (createQuoteResponse.errors) {
      throw createQuoteResponse;
    }
    return createQuoteResponse?.data || null;
  };

  const next = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const previous = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const isInitialPageLoading = !tripsData || partnerLogoLoading;

  return (
    <div className={`${appStyles.battlefacePlugin} ${bpClass}`}>
      <AUBusinessTravelContext.Provider
        value={{
          totalSteps,
          currentStep,
          quoteData,
          partnerLogo,
          partnerLogoLoading,
          setQuoteData,
          loader,
          setLoader,
          createQuote,
          isPaymentInvoice,
          setIsPaymentInvoice,
          order,
          setOrder,
          setQuoteResult,
          quoteResult,
          tripsData,
          version,
          next,
          previous,
        }}
      >
        {isInitialPageLoading ? (
          <Popup onClose={() => {}} title="" children={<LoadingModal />} closeButton={false} LoadingModal={true} />
        ) : (
          <>
            <AUHeader />
            <Steps />
          </>
        )}
      </AUBusinessTravelContext.Provider>
    </div>
  );
};

export default AUBusinessTravel;
